// Carousel - Swiper wrapper for .swiper containers: breakpoint gating via
// data-swiper-up/data-swiper-down, per-instance JSON config in
// [data-swiper-options], navigation buttons (.carousel__btn--prev/next),
// extra-content mirroring and the carousel--fan slide indexing. Behavioral
// port of v1 modules/Carousel.js.

import Swiper from 'swiper';
import { Navigation, Pagination, Parallax } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';

import { BREAKPOINTS } from '../const';

export type CarouselConfig = SwiperOptions & Record<string, unknown>;

// The `as number` casts keep v1's exact semantics for unknown breakpoint
// keys: `undefined >= x` / `undefined < x` are false -> the carousel inits.
const shouldInitialize = (
  up: string | null,
  down: string | null,
  currentWidth: number,
  breakpoints: Record<string, number>,
): boolean =>
  !(up && (breakpoints[up] as number) >= currentWidth)
  && !(down && (breakpoints[down] as number) < currentWidth);

const getTotalNumberOfSlides = (container: Element): number =>
  container.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)').length;

const defaultConfig: CarouselConfig = {
  pagination: {
    clickable: true,
  },
  a11y: true,
  loop: false,
  keyboardControl: true,
  watchSlidesProgress: true,
  spaceBetween: 40,
  modules: [Navigation, Pagination, Parallax],
};

export default class Carousel {
  selector: string;

  config: CarouselConfig;

  instances = new Set<Swiper>();

  constructor(selector = '.swiper', config: CarouselConfig = {} as CarouselConfig) {
    this.selector = selector;
    this.config = { ...defaultConfig, ...config };

    this._init();

    this.instances.forEach(instance => {
      instance.navigation.update();
    });
  }

  private _init(): void {
    const swipers = document.querySelectorAll<HTMLElement>(this.selector);

    swipers.forEach(swiper => {
      if (!this._shouldInitializeSwiper(swiper)) return;

      const swiperConfig = this._createSwiperConfig(swiper);
      const instance = this._createSwiperInstance(swiper, swiperConfig);

      if (instance) {
        this.instances.add(instance);
      }
    });
  }

  private _shouldInitializeSwiper(swiper: HTMLElement): boolean {
    const breakpointUp = swiper.getAttribute('data-swiper-up');
    const breakpointDown = swiper.getAttribute('data-swiper-down');
    const hasSlides = swiper.querySelectorAll('.swiper-slide').length > 0;
    const notInitialized = !swiper.classList.contains('swiper-initialized');

    return (
      shouldInitialize(breakpointUp, breakpointDown, window.innerWidth, BREAKPOINTS)
      && hasSlides
      && notInitialized
    );
  }

  private _createSwiperConfig(swiper: HTMLElement): CarouselConfig {
    const options = swiper.getAttribute('data-swiper-options');
    const baseConfig = options
      ? { ...this.config, ...(JSON.parse(options) as CarouselConfig) }
      : this.config;

    return {
      ...baseConfig,
      navigation: {
        nextEl: swiper.querySelector<HTMLElement>('.carousel__btn--next'),
        prevEl: swiper.querySelector<HTMLElement>('.carousel__btn--prev'),
      },
    };
  }

  private _createSwiperInstance(swiper: HTMLElement, config: CarouselConfig): Swiper {
    const instance = new Swiper(swiper, config as SwiperOptions);
    instance.update();

    instance.on('transitionEnd', this._handleSlideChange);
    this._handleSlideChange(instance);

    if (swiper.parentElement?.classList.contains('carousel--fan')) {
      instance.on('slideChangeTransitionStart', this._handleCarouselFanSlideChange);
      this._handleCarouselFanSlideChange(instance);
    }

    return instance;
  }

  private _handleSlideChange = (instance: Swiper): void => {
    if (instance.slides.length === 0) {
      return;
    }

    const activeElement = instance.slides[instance.activeIndex];
    if (!activeElement) {
      return;
    }

    // Mirror the active slide's extra content into the shared target slot
    // (v1 attribute names kept, including the "swipper" typo - it's the
    // published HTML contract).
    const extraContent = activeElement.querySelector('[data-swipper-extra-content-source]');
    const extraContentTarget = instance.el.querySelector('[data-swipper-extra-content-target]');

    if (extraContent && extraContentTarget) {
      extraContentTarget.replaceChildren(
        ...[...extraContent.children].map(item => item.cloneNode(true)),
      );
    }
  };

  private _handleCarouselFanSlideChange = (instance: Swiper): void => {
    const container = instance.el;
    const numberOfSlides = getTotalNumberOfSlides(container);
    // v1 fix: with loopedSlides undefined, v1 computed Math.min(undefined, 5)
    // = NaN and Array(NaN) threw on every fan slide change. Default to 0.
    const loopedSlides = (instance as Swiper & { loopedSlides?: number }).loopedSlides ?? 0;
    const numberOfActiveSlides = Math.min(loopedSlides, 5);
    const activeIndex = instance.realIndex;
    const offset = Math.floor(numberOfActiveSlides / 2);

    // Clear old fan indexes
    container.querySelectorAll('[data-carousel-fan-index]').forEach(slide => {
      slide.removeAttribute('data-carousel-fan-index');
    });

    // Calculate active slide indexes with wrapping
    const activeSlideIndexes = [...Array(numberOfActiveSlides)].map((_, i) => {
      const index = activeIndex - offset + i;
      return Carousel._normalizeSlideIndex(index, numberOfSlides);
    });

    // Apply fan indexes to visible slides
    activeSlideIndexes.forEach((slideIndex, position) => {
      const slide = container.querySelector(
        `.swiper-slide-visible[data-swiper-slide-index="${slideIndex}"]`,
      );

      if (slide) {
        slide.setAttribute('data-carousel-fan-index', String(position - offset));
      }
    });
  };

  private static _normalizeSlideIndex(index: number, totalSlides: number): number {
    if (index < 0) {
      return totalSlides + index;
    }
    if (index >= totalSlides) {
      return index - totalSlides;
    }
    return index;
  }

  update(): void {
    this.instances.forEach(instance => {
      instance.update();
    });
    this._init();
  }

  destroy(): void {
    this.instances.forEach(instance => instance.destroy());
    this.instances.clear();
  }
}
